<template>
    <teleport to="body">
        <div v-if="visible" class="context-menu" :style="{ top: y + 'px', left: x + 'px' }" @click.stop
            @contextmenu.prevent>

            <div class="context-menu-item" @click="setAsSourceNode">
                <span class="menu-icon">🎯</span>
                设为源节点
            </div>
            <div class="context-menu-item" @click="setAsTargetNode">
                <span class="menu-icon">🎯</span>
                设为目标节点
            </div>
            <div class="context-menu-item" @click="addTagToNode">
                <span class="menu-icon">🏷️</span>
                添加标签
            </div>
            <div class="context-menu-item" @click="deleteNode">
                <span class="menu-icon">🗑️</span>
                删除节点
            </div>
            <div class="context-menu-item" @click="expandNode">
                <span class="menu-icon">🔍</span>
                拓展节点
            </div>
            <div class="context-menu-item" @click="editNode">
                <span class="menu-icon">✏️</span>
                编辑节点
            </div>
            <div class="context-menu-item" @click="toggleNodeLock">
                <span class="menu-icon">{{ node?.locked ? '🔓' : '🔒' }}</span>
                {{ node?.locked ? '解锁节点' : '锁定节点' }}
            </div>
            <div class="context-menu-item" @click="setAsTag">
                <span class="menu-icon">🏷️</span>
                设为标签
            </div>
            <div class="context-menu-item submenu-trigger">
                <span class="menu-icon">🎯</span>
                群组管理
                <div class="context-submenu">
                    <div v-for="group in groups" :key="group.id" class="context-menu-item" :class="{
                        'group-in': isNodeInGroup(node, group.id),
                        'group-out': !isNodeInGroup(node, group.id),
                    }" @click="() => toggleNodeGroup(group.id)">
                        <span class="menu-icon">{{
                            isNodeInGroup(node, group.id) ? '🔵' : '⚪'
                            }}</span>
                        {{ group.name }}
                    </div>
                </div>
            </div>
            <div class="context-menu-item submenu-trigger">
                <span class="menu-icon">🏷️</span>
                标签管理
                <div class="context-submenu">
                    <div v-for="tag in graphTypes" :key="tag.id" class="context-menu-item"
                        @click="() => handleTagSelection(tag)">
                        <span class="menu-icon">{{ isNodeInTag(tag.id) ? '🔵' : '⚪' }}</span>
                        {{ tag.name || tag.label }}
                    </div>
                </div>
            </div>
        </div>
    </teleport>
</template>

<script>
import { defineComponent, watch, onUnmounted } from 'vue';

export default defineComponent({
    name: 'ContextMenu',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        x: {
            type: Number,
            default: 0,
        },
        y: {
            type: Number,
            default: 0,
        },
        node: {
            type: Object,
            default: null,
        },
        groups: {
            type: Array,
            default: () => [],
        },
        graphTypes: {
            type: Array,
            default: () => [],
        },
    },
    emits: [
        'set-as-source-node',
        'set-as-target-node',
        'add-tag-to-node',
        'delete-node',
        'expand-node',
        'edit-node',
        'toggle-node-lock',
        'set-as-tag',
        'toggle-node-group',
        'handle-tag-selection',
        'close-menu',
    ],
    setup(props, { emit }) {
        // 检查节点是否属于某个群组
        const isNodeInGroup = (node, groupId) => {
            if (!node || !node.groups || !Array.isArray(node.groups)) return false;
            return node.groups.includes(groupId);
        };

        // 检查节点是否具有指定标签
        const isNodeInTag = (tagId) => {
            if (!props.node || !props.node.category) return false;

            if (Array.isArray(props.node.category)) {
                return props.node.category.includes(tagId);
            } else {
                return props.node.category === tagId;
            }
        };

        // 各种菜单项的处理函数
        const setAsSourceNode = () => {
            emit('set-as-source-node');
            emit('close-menu');
        };

        const setAsTargetNode = () => {
            emit('set-as-target-node');
            emit('close-menu');
        };

        const addTagToNode = () => {
            emit('add-tag-to-node');
            emit('close-menu');
        };

        const deleteNode = () => {
            emit('delete-node');
            emit('close-menu');
        };

        const expandNode = () => {
            emit('expand-node');
            emit('close-menu');
        };

        const editNode = () => {
            emit('edit-node');
            emit('close-menu');
        };

        const toggleNodeLock = () => {
            emit('toggle-node-lock');
            emit('close-menu');
        };

        const setAsTag = () => {
            emit('set-as-tag');
            emit('close-menu');
        };

        const toggleNodeGroup = (groupId) => {
            emit('toggle-node-group', groupId);
            emit('close-menu');
        };

        const handleTagSelection = (tag) => {
            emit('handle-tag-selection', tag);
            emit('close-menu');
        };

        // 处理点击外部区域关闭菜单
        const handleClickOutside = (event) => {
            if (!event.target.closest('.context-menu')) {
                emit('close-menu');
            }
        };

        // 监听visible属性变化，动态添加/移除事件监听器
        watch(() => props.visible, (newVisible) => {
            setTimeout(() => {
                if (newVisible) {
                    // 菜单变为可见时，添加点击监听器
                    document.addEventListener('click', handleClickOutside);
                } else {
                    // 菜单变为不可见时，移除点击监听器
                    document.removeEventListener('click', handleClickOutside);
                }
            }, 10); // 延迟10ms确保DOM已更新
        }, { immediate: true }); // 立即执行

        onUnmounted(() => {
            // 组件卸载时确保移除监听器
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            isNodeInGroup,
            isNodeInTag,
            setAsSourceNode,
            setAsTargetNode,
            addTagToNode,
            deleteNode,
            expandNode,
            editNode,
            toggleNodeLock,
            setAsTag,
            toggleNodeGroup,
            handleTagSelection,
        };
    },
});
</script>

<style scoped>
.context-menu {
    position: fixed;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 9999;
    min-width: 180px;
}

.context-menu-item {
    padding: 8px 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;
}

.context-menu-item:hover {
    background-color: #f5f5f5;
}

.menu-icon {
    width: 16px;
    display: inline-block;
}

.submenu-trigger {
    position: relative;
}

.submenu-trigger .context-submenu {
    display: none;
}

.submenu-trigger:hover .context-submenu {
    display: block;
    position: absolute;
    left: 100%;
    top: 0;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    z-index: 10000;
    min-width: 150px;
}

.group-in {
    background-color: #e8f5e9;
}

.group-out {
    background-color: white;
}
</style>